export default function PullMarketData(
    marketData,
    relevantKeys,
    disasterDeclarations = [] // Make it optional to maintain backward compatibility
) {
    // Early return if no data
    if (!marketData || !relevantKeys) return null;

    // Create enhanced keys object
    const enhancedKeys = relevantKeys.map(key => {
        // Initialize the key object with existing properties
        const enhancedKey = {
            ...key,
            data: [], // Full chronological list
            yearlyData: [], // Array of year objects with data arrays
            trend: {  // New trend object
                slope: 0,
                intercept: 0,
                trendLine: []
            }
        };

        // Create temporary object to group data by year
        const yearGroups = {};

        // Iterate through market data
        marketData.forEach(dataPoint => {
            // Extract year and month from period_end
            const [year, month] = dataPoint.period_end.split('-').map(num => parseInt(num));

            let value, yoy, mom;

            // Check if this is a ratio calculation
            if (key.ratioDatapoints && Array.isArray(key.ratioDatapoints) && key.ratioDatapoints.length === 2) {
                const [numerator, denominator] = key.ratioDatapoints;
                
                // Calculate the ratio value
                value = denominator && dataPoint[denominator] !== 0 ? 
                    dataPoint[numerator] / dataPoint[denominator] : 
                    null;

                // Calculate YoY ratio
                const numeratorYoY = dataPoint[`${numerator}_yoy`];
                const denominatorYoY = dataPoint[`${denominator}_yoy`];
                yoy = denominatorYoY && denominatorYoY !== 0 ? 
                    numeratorYoY / denominatorYoY : 
                    null;

                // Calculate MoM ratio
                const numeratorMoM = dataPoint[`${numerator}_mom`];
                const denominatorMoM = dataPoint[`${denominator}_mom`];
                mom = denominatorMoM && denominatorMoM !== 0 ? 
                    numeratorMoM / denominatorMoM : 
                    null;
            } else {
                // Standard single metric
                value = dataPoint[key.id];
                yoy = dataPoint[`${key.id}_yoy`];
                mom = dataPoint[`${key.id}_mom`];
            }

            // Create data point object with calculated values
            const pointData = {
                value,
                yoy,
                mom,
                year,
                month,
                date: dataPoint.period_end
            };

            // Add to chronological list
            enhancedKey.data.push(pointData);

            // Group by year
            if (!yearGroups[year]) {
                yearGroups[year] = [];
            }
            yearGroups[year].push(pointData);
        });

        // Convert year groups to array format
        enhancedKey.yearlyData = Object.entries(yearGroups).map(([year, data]) => ({
            year: parseInt(year),
            data: data.sort((a, b) => a.month - b.month)
        }));

        // Sort chronological data by date
        enhancedKey.data.sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
        });

        // Sort yearlyData by year
        enhancedKey.yearlyData.sort((a, b) => a.year - b.year);

        // Calculate trend
        const values = enhancedKey.data.map(point => point.value);
        const n = values.length;
        let sumX = 0;
        let sumY = 0;
        let sumXY = 0;
        let sumXX = 0;

        values.forEach((value, i) => {
            if (value !== null) {  // Skip null values
                sumX += i;
                sumY += value;
                sumXY += i * value;
                sumXX += i * i;
            }
        });

        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        enhancedKey.trend = {
            slope,
            intercept,
            trendLine: values.map((_, i) => slope * i + intercept)
        };

        return enhancedKey;
    });

    // If we don't have disaster declarations, return the existing enhanced keys
    if (!disasterDeclarations.length) {
        // console.log('No disaster declarations provided, returning standard analysis');
        return enhancedKeys;
    }

    // console.log('Starting disaster trend analysis with declarations:', disasterDeclarations);

    // Get the first and last data points' month/year for comparison
    const firstDataPoint = marketData[0];
    const lastDataPoint = marketData[marketData.length - 1];
    
    const firstDataYear = lastDataPoint.year;
    const firstDataMonth = lastDataPoint.month;
    const lastDataYear = firstDataPoint.year;
    const lastDataMonth = firstDataPoint.month;

    // console.log('Data range:', { 
    //     start: { year: firstDataYear, month: firstDataMonth },
    //     end: { year: lastDataYear, month: lastDataMonth }
    // });

    // Sort disasters chronologically by year/month, excluding pre-2021 events
    const sortedDisasters = [...disasterDeclarations]
        .filter(disaster => disaster.year >= 2021)
        .sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
        });

    // console.log('Sorted disasters (2021 and later):', sortedDisasters);

    // Find disaster windows (groups within 3 months of first event)
    const disasterWindows = [];
    let currentWindow = [];
    let currentWindowEndMonth = null;
    let currentWindowEndYear = null;

    sortedDisasters.forEach((disaster) => {
        if (currentWindow.length === 0) {
            // Start new window
            currentWindow.push(disaster);
            // Set window end date to 3 months after first disaster
            currentWindowEndMonth = disaster.month + 3;
            currentWindowEndYear = disaster.year;
            if (currentWindowEndMonth > 12) {
                currentWindowEndMonth -= 12;
                currentWindowEndYear += 1;
            }
        } else {
            // Check if this disaster is within the current window's end
            const monthsFromStart = 
                (disaster.year - currentWindow[0].year) * 12 + 
                (disaster.month - currentWindow[0].month);
            
            if (monthsFromStart <= 3) {
                currentWindow.push(disaster);
            } else {
                // This disaster starts a new window
                disasterWindows.push([...currentWindow]);
                currentWindow = [disaster];
                currentWindowEndMonth = disaster.month + 3;
                currentWindowEndYear = disaster.year;
                if (currentWindowEndMonth > 12) {
                    currentWindowEndMonth -= 12;
                    currentWindowEndYear += 1;
                }
            }
        }
    });

    // Don't forget the last window
    if (currentWindow.length > 0) {
        disasterWindows.push(currentWindow);
    }

    // console.log('Identified disaster windows:', disasterWindows);

    // Analyze each key's data against the disaster windows
    enhancedKeys.forEach(key => {
        // console.log(`Analyzing disaster impact for ${key.label}`);
        // console.log("Analyzing disaster impact for: ", key);
        
        const windowAnalyses = disasterWindows.map((window, index) => {
            const startYear = window[0].year;
            const startMonth = window[0].month;

            const windowDisasterNumbers = window.map(disaster => disaster.disasterNumber);
            
            // console.log(`Analyzing window starting ${startMonth}/${startYear}`);
            
            // Calculate months between data start and window start (for before data check)
            const monthsBeforeStart = 
                (startYear - firstDataYear) * 12 + 
                (startMonth - firstDataMonth);

            // Calculate months between window start and last data point (for after data check)
            const monthsAfterStart = 
                (lastDataYear - startYear) * 12 + 
                (lastDataMonth - startMonth);

            // console.log('Months calculations:', {
            //     monthsBeforeStart,
            //     monthsAfterStart,
            //     firstData: { year: firstDataYear, month: firstDataMonth },
            //     lastData: { year: lastDataYear, month: lastDataMonth },
            //     windowStart: { year: startYear, month: startMonth }
            // });

            // Get data points before and after window start
            const beforeData = key.data.filter(point => {
                const [pointYear, pointMonth] = point.date.split('-').map(num => parseInt(num));
                return (pointYear < startYear) || 
                       (pointYear === startYear && pointMonth < startMonth);
            });

            const afterData = key.data.filter(point => {
                const [pointYear, pointMonth] = point.date.split('-').map(num => parseInt(num));
                return (pointYear > startYear) || 
                       (pointYear === startYear && pointMonth >= startMonth);
            });

            // console.log('Data validation:', {
            //     beforeDataPoints: beforeData.length,
            //     afterDataPoints: afterData.length,
            //     monthsAfterStart,
            //     requiresBeforePoints: 16,
            //     requiresAfterPoints: 6,
            //     hasEnoughBeforeData: beforeData.length >= 16,
            //     hasEnoughAfterData: monthsAfterStart >= 6
            // });

            // Check for sufficient data
            if (beforeData.length < 12) {
                // console.log('Insufficient before data - skipping window');
                return null;
            }

            if (monthsAfterStart < 15) {
                // console.log('Too recent - skipping window');
                return null;
            }

            // Calculate averages and trends
            const beforeAvg = beforeData.reduce((sum, point) => sum + point.value, 0) / beforeData.length;
            const afterAvg = afterData.reduce((sum, point) => sum + point.value, 0) / afterData.length;

            const beforeTrend = calculateTrend(beforeData.map(point => point.value));
            const afterTrend = calculateTrend(afterData.map(point => point.value));

            const analysis = {
                windowIndex: index + 1,
                windowStart: { year: startYear, month: startMonth },
                disasters: windowDisasterNumbers,
                beforeAverage: beforeAvg,
                afterAverage: afterAvg,
                averageChange: afterAvg - beforeAvg,
                beforeSlope: beforeTrend.slope,
                beforeCAGR: beforeTrend.cagr,
                afterSlope: afterTrend.slope,
                afterCAGR: afterTrend.cagr,
                slopeChange: afterTrend.slope - beforeTrend.slope,
                cagrChange: afterTrend.cagr - beforeTrend.cagr,
                direction: afterTrend.slope > beforeTrend.slope ? 'up' : 'down'
            };

            return analysis;
        }).filter(Boolean); // Remove null analyses

        // Separate windows by direction
        const upwardWindows = windowAnalyses.filter(window => window.direction === 'up');
        const downwardWindows = windowAnalyses.filter(window => window.direction === 'down');

        // Handle window analysis based on count
        let earliestUpward = null;
        let earliestUpwardIndex = null;
        let earliestDownward = null;
        let earliestDownwardIndex = null;
        let mostSignificantUpward = null;
        let mostSignificantUpwardIndex = null;
        let mostSignificantDownward = null;
        let mostSignificantDownwardIndex = null;

        if (upwardWindows.length === 1) {
            // If only one upward window, it's both earliest and most significant
            earliestUpward = upwardWindows[0];
            earliestUpwardIndex = earliestUpward.windowIndex;
            mostSignificantUpward = upwardWindows[0];
            mostSignificantUpwardIndex = mostSignificantUpward.windowIndex;
        } else if (upwardWindows.length > 1) {
            // Find earliest by minimum windowIndex
            earliestUpward = upwardWindows.reduce((min, window) => 
                window.windowIndex < min.windowIndex ? window : min
            );
            earliestUpwardIndex = earliestUpward.windowIndex;
            // Find most significant by maximum averageChange
            mostSignificantUpward = upwardWindows.reduce((max, window) => 
                Math.abs(window.averageChange) > Math.abs(max.averageChange) ? window : max
            );
            mostSignificantUpwardIndex = mostSignificantUpward.windowIndex;
        }

        if (downwardWindows.length === 1) {
            // If only one downward window, it's both earliest and most significant
            earliestDownward = downwardWindows[0];
            earliestDownwardIndex = earliestDownward.windowIndex;
            mostSignificantDownward = downwardWindows[0];
            mostSignificantDownwardIndex = mostSignificantDownward.windowIndex;
        } else if (downwardWindows.length > 1) {
            // Find earliest by minimum windowIndex
            earliestDownward = downwardWindows.reduce((min, window) => 
                window.windowIndex < min.windowIndex ? window : min
            );
            // Find most significant by maximum averageChange
            mostSignificantDownward = downwardWindows.reduce((max, window) => 
                Math.abs(window.averageChange) > Math.abs(max.averageChange) ? window : max
            );
            earliestDownwardIndex = earliestDownward.windowIndex;
            mostSignificantDownwardIndex = mostSignificantDownward.windowIndex;
        }
        // console.log('windowAnalyses', {
        //     earliestUpward,
        //     earliestDownward,
        //     mostSignificantUpward,
        //     mostSignificantDownward
        // });

        // Add disaster analysis to the key
        key.disasterAnalysis = {
            totalWindows: windowAnalyses.length,
            upwardWindows: upwardWindows.length,
            downwardWindows: downwardWindows.length,
            // earliestUpward: earliestUpward || null,
            earliestUpwardWindowIndex: earliestUpwardIndex || null,
            // earliestDownward: earliestDownward || null,
            earliestDownwardWindowIndex: earliestDownwardIndex || null,
            // mostSignificantUpward: mostSignificantUpward || null,
            mostSignificantUpwardWindowIndex: mostSignificantUpwardIndex || null,
            // mostSignificantDownward: mostSignificantDownward || null,
            mostSignificantDownwardWindowIndex: mostSignificantDownwardIndex || null,
            allWindows: windowAnalyses
        };

        // console.log(`Completed disaster analysis for ${key.label}:`, key.disasterAnalysis);
    });

    return enhancedKeys;
}

// Helper function to calculate trend and CAGR
function calculateTrend(values) {
    const n = values.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumXX = 0;

    // Filter out null values first
    const validValues = values.filter(value => value !== null);
    const startValue = validValues[0];
    const endValue = validValues[validValues.length - 1];
    const monthsPassed = validValues.length - 1;

    // Calculate CAGR
    // CAGR = (endValue/startValue)^(1/years) - 1
    // where years = months/12
    const cagr = Math.pow(endValue / startValue, 12/monthsPassed) - 1;

    validValues.forEach((value, i) => {
        sumX += i;
        sumY += value;
        sumXY += i * value;
        sumXX += i * i;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return { 
        slope, 
        intercept,
        cagr: isNaN(cagr) ? 0 : cagr // Handle edge cases where calculation might fail
    };
}